import React, { useState, useEffect, useRef } from 'react';
import '../css/App.css';
// Note: print.css import removed as printing is no longer handled here
import Form from './Form.js';
import Resume from './Resume.js';
import Login, { useCiamLogin } from './login.js';
import HomeScreen from './HomeScreen.js';
import JobSeriesScreen from './JobSeriesScreen.js';
import SkillsScreen from './skillsScreen.js';
import O4SkillsScreen from "./o4SkillsScreen.js";
import TailorResume from './tailorResume.js';
import OneShotResume from './OneShotResume.js';
import HamburgerMenu from './HamburgerMenu.js';
import InfoScreen from './InfoScreen.js';
import axios from 'axios';
import EditResume from './EditResume';
import fetchTokenUsage from './tokenUsage';
// Import updateTokenUsage so we can refresh usage when user changes
import fetchTokenThreshold from './fetchTokenThreshold.js';
// NEW: Inspector (AI validation stub UI)
import Inspector from './Inspector';
// Import docx and file-saver for DOCX generation
import { Packer } from 'docx';
import { saveAs } from 'file-saver';
// Import helper to build the resume document
import buildResumeDoc from './buildResumeDoc.js';
import { buildResumeDownloadFilename } from './resumeFilename.js';
import inferResumeTitle from './resumeTitleInference.js';
import { normalizeResumeFormat, RESUME_FORMATS } from './resumeFormat.js';
import { SAVE_LOAD_FUNCTIONS_BASE_URL as FUNCTIONS_BASE_URL_SAVE_LOAD } from '../config/api';



// Canonical list of section ids the app supports (used to validate/repair sectionOrder)
const ALL_SECTIONS = [
  'personalInfo',
  'professionalSummary',
  'skills',
  'workHistory',
  'educationHistory',
  'certifications',
  'training',
  'volunteerWork',
  'languages',
  // New simple list sections for user hobbies and achievements
  'interests',
  'awards',
  'organizations',
  'references',
  'additionalInformation',
  'militaryInfo'
];

const DEFAULT_SECTION_ORDER = [
  'personalInfo',
  'professionalSummary',
  'skills',
  'workHistory',
  'educationHistory',
  'certifications',
  'training',
  'volunteerWork',
  'languages',
  // Place interests and awards immediately after languages by default
  'interests',
  'awards',
  'organizations',
  'references',
  'additionalInformation',
  'militaryInfo'
];

// Predefined section orders for different resume layout templates. Each template
// defines a preferred ordering for the major resume sections. When a
// template is selected, the application will update the formData.sectionOrder
// accordingly. The arrays here include all supported sections in the desired
// order for the given resume type. Any sections missing from these
// definitions will be appended automatically by the sectionOrder repair
// effect defined below. Feel free to adjust the orderings as needed.
const TEMPLATE_SECTION_ORDERS = {
  // Default/Professional layout: places the professional summary first,
  // followed by skills, work history and education. Remaining sections
  // follow the original default ordering.
  professional: [
    'personalInfo',
    'professionalSummary',
    'skills',
    'workHistory',
    'educationHistory',
    'certifications',
    'training',
    'volunteerWork',
    'languages',
    'interests',
    'awards',
    'organizations',
    'references',
    'additionalInformation',
    'militaryInfo'
  ],
  // Student layout: emphasises skills and education at the top. A
  // professional summary may still be included but appears after the
  // education section. Remaining sections follow the original order.
  student: [
    'personalInfo',
    'skills',
    'educationHistory',
    'professionalSummary',
    'workHistory',
    'certifications',
    'training',
    'volunteerWork',
    'languages',
    'interests',
    'awards',
    'organizations',
    'references',
    'additionalInformation',
    'militaryInfo'
  ],
  // Military layout: places the professional summary and military
  // information up front. Skills, work history and education follow. The
  // remaining sections maintain their default order. This helps highlight
  // military service in a prominent position.
  military: [
    'personalInfo',
    'militaryInfo',
    'professionalSummary',
    'skills',
    'workHistory',
    'educationHistory',
    'certifications',
    'training',
    'volunteerWork',
    'languages',
    'interests',
    'awards',
    'organizations',
    'references',
    'additionalInformation'
  ],
};

const SLOT_NAME_MAX_LENGTH = 60;
const DEFAULT_RESUME_SLOT_COUNT = 5;

const normalizeResumeSlotCount = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < DEFAULT_RESUME_SLOT_COUNT) {
    return DEFAULT_RESUME_SLOT_COUNT;
  }
  return parsed;
};

const sanitizeSlotDisplayName = (value) =>
  Array.from(String(value ?? ''))
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || (code > 31 && code !== 127);
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SLOT_NAME_MAX_LENGTH);

const normalizeResumeSlotNames = (value) => {
  let source = value;
  if (typeof value === 'string') {
    try {
      source = JSON.parse(value);
    } catch {
      source = {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  return Object.entries(source).reduce((names, [slot, name]) => {
    const cleaned = sanitizeSlotDisplayName(name);
    if (cleaned) names[String(slot)] = cleaned;
    return names;
  }, {});
};

const getFallbackResumeSlotLabel = (slot) =>
  String(slot) === 'base' ? 'Base Profile' : `Resume ${slot}`;

const App = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [appNotice, setAppNotice] = useState(null);
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      authority: []
    },
    militaryInfo: {
      militaryStatus: '',
      militaryBranch: '',
      militaryRank: '',  // New field for military rank
      militaryDuration:'',
      militaryOccupation: '',
      militaryLocation: '',
    },
    educationHistory: [],  
    workHistory: [],  
    volunteerWork: [],  
    skills: [],  
    selectedSkills: [],  
    certifications: [],  
    training: [],  
    references: [],  
    languages: [],  
    // Arrays for new Interests/Hobbies and Awards/Honors phases
    interests: [],
    awards: [],
    organizations: [],  
    additionalInformation: '',  
    selectedJobSeries: [],  
    parsedJobAnnouncement: {
      raw: null,
      aiFormatted: null
    },
    resumeEnhancementTips: '', 
    specializedExperience: '', 
    competencies: [], 
    knowledge: [], 
    keywords: [], 
    // Additional generic fields for job posting requirements
    jobExperienceRequirements: '',
    jobCompetencies: [],
    jobKnowledge: [],
    // Fields for private‑sector job description parsing
    experienceRequirements: '',
    softSkills: [],
    requiredSkills: [],
    preferredSkills: [],
    educationRequirements: '',
    jobSummary: '',
    jobKeywords: [], // new keywords extracted from job description
    selectedJobKeywords: [], // user‑selected keywords for bullet generation
    series: '',
    combinedManualText: '',
    manualSpecializedExperienceRaw: '', 
    manualCompetenciesRaw: '',          
    manualKnowledgeRaw: '',             
    manualKeywordsRaw: '',  
    // Additional manual fields matching the generic job posting fields
    manualJobExperienceRequirementsRaw: '',
    manualJobCompetenciesRaw: '',
    manualJobKnowledgeRaw: '',
    manualJobKeywordsRaw: '',
    user:'',
    selectedWorkHistoryIndex:'',
    selectedWorkHistory:'',
    // Professional summary field
    professionalSummary: '',
    showProfessionalSummary: true, 

    // Controls output detail without removing any saved profile fields.
    resumeFormat: RESUME_FORMATS.FEDERAL,

    // NEW: user-configurable section ordering
    sectionOrder: [...DEFAULT_SECTION_ORDER],
  });

  // Track which resume layout template is currently selected. The default
  // selection uses the professional layout. Changing this state does not
  // automatically update the resume order; instead, the applyTemplate
  // function below updates formData.sectionOrder when a user explicitly
  // chooses a new layout.
  const [selectedTemplate, setSelectedTemplate] = useState('professional');

  const SECTION_LABELS = {
  personalInfo: "Personal Information",
  professionalSummary: "Professional Summary",
  skills: "Skills",
  workHistory: "Work Experience",
  educationHistory: "Education",
  certifications: "Certifications",
  training: "Training",
  volunteerWork: "Volunteer Experience",
  languages: "Languages",
  interests: "Interests",
  awards: "Awards",
  organizations: "Organizations",
  references: "References",
  additionalInformation: "Additional Information",
  militaryInfo: "Military Service",
};


  // ---- Section order helpers ----
  // Ensure sectionOrder always contains every supported section exactly once
  useEffect(() => {
    const current = formData.sectionOrder || [];
    // Add any missing sections (at the end)
    const withMissing = [...current, ...ALL_SECTIONS.filter(s => !current.includes(s))];
    // Remove any unknown/duplicated sections while keeping first occurrence order
    const seen = new Set();
    const cleaned = withMissing.filter(s => {
      if (!ALL_SECTIONS.includes(s) || seen.has(s)) return false;
      seen.add(s);
      return true;
    });
    // Only update if something actually changed
    if (JSON.stringify(cleaned) !== JSON.stringify(current)) {
      setFormData(prev => ({ ...prev, sectionOrder: cleaned }));
    }
  }, [formData.sectionOrder]); // runs when sectionOrder changes

  const [announcementData, ] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showResume, setShowResume] = useState(true);
  const viewMode = 'multi';
  const [currentScreen, setCurrentScreen] = useState('home');
  const [jobSeriesAvailable, setJobSeriesAvailable] = useState(false);

  // NEW: toggle inline editing for Resume preview
  const [resumeEditable, setResumeEditable] = useState(false);

  // State to control visibility of the side pane (resume preview and results)
  // When true the right-hand pane is rendered; when false it is hidden entirely.
  const [paneVisible, setPaneVisible] = useState(false);

  // NEW: state to track whether the sticky controls panel is expanded on small
  // screens. When false only a toggle button is shown; when true the full
  // controls panel is visible. This does not impact the behaviour on
  // larger viewports.
  const [controlsExpanded, setControlsExpanded] = useState(false);

  // Refs used to detect clicks outside of the controls panel. When a click
  // occurs outside both the toggle button and the controls content, the
  // expanded panel should automatically collapse. This improves usability on
  // mobile devices where screen real estate is limited.
  const controlsRef = useRef(null);
  const toggleRef = useRef(null);

  // Collapse the controls panel when clicking anywhere outside of the toggle
  // button or the panel itself. The listener is attached when the component
  // mounts and removed on cleanup. It depends on the current expansion state.
  useEffect(() => {
    function handleClickOutside(event) {
      if (controlsExpanded) {
        // If the click was outside of both the controls pane and the toggle
        // button, close the pane.
        if (
          controlsRef.current &&
          !controlsRef.current.contains(event.target) &&
          toggleRef.current &&
          !toggleRef.current.contains(event.target)
        ) {
          setControlsExpanded(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [controlsExpanded]);

  useEffect(() => {
    if (formData.educationHistory.length > 0) {
      setJobSeriesAvailable(true);
    } else {
      setJobSeriesAvailable(false);
    }
  }, [formData.educationHistory]);

  const navigateToScreen = (screen) => {
    setCurrentScreen(screen);
    setPaneVisible(false);
    setResumeEditable(false);
    setControlsExpanded(false);
  };

  /**
   * Build and download a DOCX version of the resume. This function uses the
   * buildResumeDoc helper to construct a Word document from the current
   * formData, converts it to a Blob using docx's Packer, and saves it via
   * file-saver. A sensible filename is chosen based on the user's last name.
   */
const downloadDocx = async () => {
  try {
    setAppNotice({ type: 'status', message: 'Preparing your DOCX download.' });
    let downloadData = formData;
    if (!String(formData.aiInferredResumeTitle || '').trim()) {
      try {
        const inferredTitle = await inferResumeTitle(formData);
        if (inferredTitle) {
          downloadData = { ...formData, aiInferredResumeTitle: inferredTitle };
          setFormData((previous) => ({
            ...previous,
            aiInferredResumeTitle: inferredTitle,
          }));
        }
      } catch (titleError) {
        console.warn('AI resume title inference was unavailable; using a neutral filename.');
      }
    }
    const doc = buildResumeDoc(downloadData); // already imported
    const blob = await Packer.toBlob(doc); // from 'docx'
    const fileName = buildResumeDownloadFilename({
      formData: downloadData,
    });
    const mime =
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    // 1) Try the Web Share API (best UX on mobile)
    if (typeof navigator !== "undefined" && navigator.canShare) {
      try {
        const file = new File([blob], fileName, { type: mime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "My Resume",
            text: "DOCX resume",
            files: [file],
          });
          return; // done
        }
      } catch {
        /* fall through to saveAs */
      }
    }

    // 2) Cross-browser download (handles iOS Safari better than manual <a>)
    saveAs(blob, fileName);
    setAppNotice({ type: 'status', message: 'Your resume download is ready.' });

  } catch (err) {
    console.error("Error generating DOCX:", err);
    setAppNotice({ type: 'error', message: 'The DOCX could not be generated. Please try again.' });
  }
};


  const [, setTokenUsage] = useState(0); // Current token usage
  const [tokenThreshold, setTokenThreshold] = useState(260000); // Maximum tokens allowed
  const [, setUsagePercentage] = useState(0); // Usage as percentage
  const [selectedResumeSlot, setSelectedResumeSlot] = useState('base'); // New state to track selected resume slot
  const [resumeSlotNames, setResumeSlotNames] = useState({});
  const [resumeVersions, setResumeVersions] = useState({});
  const [slotNameDraft, setSlotNameDraft] = useState('');
  const [slotNameSaving, setSlotNameSaving] = useState(false);
  const [currentResumeName, setCurrentResumeName] = useState('Base Profile'); 

  // Use the custom hook from login.js to obtain signOut.  This hook is safe
  // to call at the top of the component body (it does not depend on props or
  // state).  We will use signOut to implement a logout button in the sticky
  // controls.
  const { signOut } = useCiamLogin();

  const getResumeSlotLabel = (slot) =>
    resumeSlotNames[String(slot)] || getFallbackResumeSlotLabel(slot);

  useEffect(() => {
    const customName = resumeSlotNames[String(selectedResumeSlot)] || '';
    setCurrentResumeName(customName || getFallbackResumeSlotLabel(selectedResumeSlot));
    setSlotNameDraft(customName);
  }, [resumeSlotNames, selectedResumeSlot]);

  useEffect(() => {
    if (!formData.user) return;
    const initializeTokenData = async () => {
      try {
        const threshold = await fetchTokenThreshold(formData.user);
        setTokenThreshold(threshold);
        const usage = await fetchTokenUsage(formData.user, threshold);
        setTokenUsage(usage.tokenUsage);
        setUsagePercentage(usage.usagePercentage);
      } catch (error) {
        console.error('Error initializing token data:', error);
      }
    };
    initializeTokenData();
  }, [formData.user]);

  const [numberOfResumeSlots, setNumberOfResumeSlots] = useState(DEFAULT_RESUME_SLOT_COUNT);

  useEffect(() => {
    if (!formData.user) return;
    const fetchResumeSlots = async () => {
      try {
        const response = await axios.post(`${FUNCTIONS_BASE_URL_SAVE_LOAD}/api/saveLoadFunctionAppHttp`, {
          action: 'getnumberofresumeslots',
          tablePartition: 'userResume',
          userID: formData.user,
          tableName: 'userProfiles',
        });
        setNumberOfResumeSlots(normalizeResumeSlotCount(response.data.numberOfResumeSlots));
        setResumeSlotNames(normalizeResumeSlotNames(response.data.resumeSlotNames));
      } catch (error) {
        console.error('Failed to fetch resume slots:', error);
        setNumberOfResumeSlots(DEFAULT_RESUME_SLOT_COUNT);
        setResumeSlotNames({});
      }
    };
    fetchResumeSlots();
  }, [formData.user]);

  // Handler to change the selected resume slot.
  const handleSlotChange = (event) => {
    const nextSlot = event.target.value;
    setSelectedResumeSlot(nextSlot);
    setCurrentResumeName(getResumeSlotLabel(nextSlot));
    setSlotNameDraft(resumeSlotNames[String(nextSlot)] || '');
  };

  const handleLoad = async () => {
    const requestBody = {
      action: "load",
      tablePartition: "userResume",
      userID: formData.user,
      tableName: "userProfiles",
      resumeSlot: selectedResumeSlot
    };

    try {
      const response = await axios.post(`${FUNCTIONS_BASE_URL_SAVE_LOAD}/api/saveLoadFunctionAppHttp`, requestBody, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        const loadedResume = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
        const loadedVersion = response.headers?.['x-resume-version'] || '';
        setResumeVersions((versions) => ({
          ...versions,
          [String(selectedResumeSlot)]: loadedVersion,
        }));
        setFormData({
          ...loadedResume,
          resumeFormat: normalizeResumeFormat(loadedResume.resumeFormat),
          __loadedFromServer: true,
        });
        setCurrentResumeName(getResumeSlotLabel(selectedResumeSlot));
        setAppNotice({ type: 'status', message: 'Resume loaded successfully.' });
      } else {
        setAppNotice({ type: 'error', message: 'The saved resume is missing or invalid.' });
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setAppNotice({ type: 'error', message: 'No saved resume was found in this slot yet.' });
      } else if (error?.response?.status === 401) {
        setAppNotice({ type: 'error', message: 'Please sign in again before loading your resume.' });
      } else {
        setAppNotice({ type: 'error', message: 'The resume could not be loaded.' });
      }
    }
  };

  const saveResumeSlotName = async (nameOverride = slotNameDraft) => {
    const cleanedName = sanitizeSlotDisplayName(nameOverride);
    setSlotNameSaving(true);
    try {
      const response = await axios.post(`${FUNCTIONS_BASE_URL_SAVE_LOAD}/api/saveLoadFunctionAppHttp`, {
        action: 'setresumeslotname',
        tablePartition: 'userResume',
        userID: formData.user,
        tableName: 'userProfiles',
        resumeSlot: selectedResumeSlot,
        slotName: cleanedName,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const nextNames = normalizeResumeSlotNames(response.data?.resumeSlotNames);
      setResumeSlotNames(nextNames);
      setSlotNameDraft(nextNames[String(selectedResumeSlot)] || '');
      setCurrentResumeName(nextNames[String(selectedResumeSlot)] || getFallbackResumeSlotLabel(selectedResumeSlot));
    } catch (error) {
      setAppNotice({ type: 'error', message: 'The resume slot name could not be saved.' });
    } finally {
      setSlotNameSaving(false);
    }
  };

  const handleSave = async () => {
    const requestBody = getRequestBody("save");
    try {
      const response = await axios.post(`${FUNCTIONS_BASE_URL_SAVE_LOAD}/api/saveLoadFunctionAppHttp`, requestBody, {
        headers: { 'Content-Type': 'application/json' }
      });
      const savedVersion = response.data?.version || response.headers?.['x-resume-version'] || '';
      if (savedVersion) {
        setResumeVersions((versions) => ({
          ...versions,
          [String(selectedResumeSlot)]: savedVersion,
        }));
      }
      setAppNotice({ type: 'status', message: 'Resume saved successfully.' });
    } catch (error) {
      if (error?.response?.status === 409) {
        setAppNotice({ type: 'error', message: 'This saved resume changed in another session. Load it again before saving.' });
      } else if (error?.response?.status === 413) {
        setAppNotice({ type: 'error', message: 'This resume is too large to save. Remove unused content and try again.' });
      } else {
        setAppNotice({ type: 'error', message: 'The resume could not be saved.' });
      }
    }
  };

  const getRequestBody = (action) => {
    return {
      action: action,
      tablePartition: "userResume",
      userID: formData.user,
      tableName: "userProfiles",
      resumeJson: JSON.stringify(formData),
      resumeSlot: selectedResumeSlot,
      resumeVersion: resumeVersions[String(selectedResumeSlot)] || '',
    };
  };

  const handleSaveEnhancementTips = (tips) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      resumeEnhancementTips: tips
    }));
  };

  // ---- UI for reordering sections ----
  const [selectedSection, setSelectedSection] = useState('workHistory');

  const moveSection = (direction) => {
    const order = formData.sectionOrder || [];
    const idx = order.indexOf(selectedSection);
    if (idx === -1) return;

    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= order.length) return;

    const next = [...order];
    const [moved] = next.splice(idx, 1);
    next.splice(targetIdx, 0, moved);

    setFormData(prev => ({ ...prev, sectionOrder: next }));
  };

  const resetSectionOrder = () => {
    setFormData(prev => ({ ...prev, sectionOrder: [...DEFAULT_SECTION_ORDER] }));
  };

  /**
   * Apply a predefined section ordering based on the chosen template. When
   * invoked, this function updates the selectedTemplate state and sets
   * formData.sectionOrder to the full ordering defined for the template. Any
   * unknown or missing sections will be automatically appended by the
   * sectionOrder repair effect defined at the top of this component.
   * @param {string} templateName The key of the desired template
   */
  const applyTemplate = (templateName) => {
    if (!TEMPLATE_SECTION_ORDERS[templateName]) return;
    setSelectedTemplate(templateName);
    // Update the section order. Spread to avoid mutating the original array.
    const nextOrder = [...TEMPLATE_SECTION_ORDERS[templateName]];
    setFormData(prev => ({ ...prev, sectionOrder: nextOrder }));
    // Optionally reset selectedSection to the first entry for convenience
    if (nextOrder.length > 0) setSelectedSection(nextOrder[0]);
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'home':
        return <HomeScreen />;
      case 'form':
        // Form refreshes loaded resume data through the __loadedFromServer flag.
        // Keep this component mounted during slot renames so display-name edits
        // do not interrupt in-progress form entry.
        return (
          <Form
            formData={formData}
            setFormData={setFormData}
            viewMode={viewMode}
            // Pass down token-related props so the Form can update usage
            setTokenUsage={setTokenUsage}
            setUsagePercentage={setUsagePercentage}
            tokenThreshold={tokenThreshold}
            // Provide a navigation handler to allow phases to redirect to other screens
            setCurrentScreen={navigateToScreen}
          />
        );
      case 'jobSeries':
        return (
          <JobSeriesScreen 
            formData={formData} 
            setFormData={setFormData} 
            setTokenUsage={setTokenUsage} 
            setUsagePercentage={setUsagePercentage} 
            tokenThreshold={tokenThreshold}
          />
        );
      case 'skills':
        return (
          <SkillsScreen 
            formData={formData} 
            setFormData={setFormData} 
            setTokenUsage={setTokenUsage} 
            setUsagePercentage={setUsagePercentage} 
            tokenThreshold={tokenThreshold}
          />
        );
       case 'o4Skills': 
       return (
        <O4SkillsScreen
          formData={formData}
          setFormData={setFormData}
          setTokenUsage={setTokenUsage}
          setUsagePercentage={setUsagePercentage}
          tokenThreshold={tokenThreshold}
        />
      );

      case 'tailorResume':
        return (
          <TailorResume
            formData={formData}
            setFormData={setFormData}
            handleSaveEnhancementTips={handleSaveEnhancementTips}
            announcementData={announcementData}
            setTokenUsage={setTokenUsage}
            setUsagePercentage={setUsagePercentage}
            tokenThreshold={tokenThreshold}
          />
        );
      case 'oneShotResume':
        return (
          <OneShotResume
            formData={formData}
            setFormData={setFormData}
            setTokenUsage={setTokenUsage}
            setUsagePercentage={setUsagePercentage}
            tokenThreshold={tokenThreshold}
          />
        );
      case 'info':
        return <InfoScreen />;
      case 'editResume':
        return <EditResume formData={formData} setFormData={setFormData} />;
      // One-Shot Preflight screen
      case 'inspector':
        return (
          <Inspector
            formData={formData}
            setFormData={setFormData}
            setCurrentScreen={setCurrentScreen}
          />
        );
      default:
        return <HomeScreen />;
    }
    // (unreachable) fetchTokenUsage={fetchTokenUsage}
  };

  const renderCenterPreview = () => (
    <section className="app-center-preview" aria-label={showResults ? 'Resume data' : 'Resume preview'}>
      <div className="app-preview-header app-center-preview-header">
        <div>
          <span>Preview</span>
          <h2>{showResults ? 'Resume Data' : 'Resume Preview'}</h2>
        </div>
        <button
          type="button"
          onClick={() => {
            setResumeEditable(false);
            setPaneVisible(false);
          }}
        >
          Return to {currentScreen === 'oneShotResume'
            ? 'One-Shot'
            : currentScreen === 'inspector'
              ? 'Preflight'
              : currentScreen}
        </button>
      </div>
      <div className="results center-results">
        {showResults && (
          <div className="a4-container">
            <h2>Results</h2>
            <pre className="a4-content">{JSON.stringify(formData, null, 2)}</pre>
          </div>
        )}

        {showResume && (
          <div className="a4-container">
            <Resume formData={formData} editable={resumeEditable} setFormData={setFormData} />
          </div>
        )}
        {announcementData && (
          <div>
            <h2>Parsed Job Announcement</h2>
            <pre>{JSON.stringify(announcementData, null, 2)}</pre>
          </div>
        )}
        {formData.resumeEnhancementTips && (
          <div>
            <h2>Resume Enhancement Tips</h2>
            <pre>{formData.resumeEnhancementTips}</pre>
          </div>
        )}
      </div>
    </section>
  );

  const cleanedSlotNameDraft = sanitizeSlotDisplayName(slotNameDraft);
  const selectedCustomSlotName = resumeSlotNames[String(selectedResumeSlot)] || '';
  const slotNameChanged = cleanedSlotNameDraft !== selectedCustomSlotName;

  if (!authenticated) {
    return <Login setAuthenticated={setAuthenticated} setFormData={setFormData} />;
  }

  return (
    <div className="App">
      <a className="skip-link" href="#app-main-content">Skip to resume workspace</a>
      {appNotice && (
        <div
          className={`app-notice ${appNotice.type === 'error' ? 'is-error' : ''}`}
          role={appNotice.type === 'error' ? 'alert' : 'status'}
        >
          <span>{appNotice.message}</span>
          <button type="button" onClick={() => setAppNotice(null)} aria-label="Dismiss notification">
            Dismiss
          </button>
        </div>
      )}
      <div className={`app-console-shell ${paneVisible ? 'preview-open' : ''}`}>
        <aside className="app-left-rail" aria-label="Application navigation">
          <div className="rail-kicker">Menu</div>
          <HamburgerMenu
            setCurrentScreen={navigateToScreen}
            jobSeriesAvailable={jobSeriesAvailable}
          />
          <div className="rail-status">
            <span>Screen</span>
            <strong>
              {currentScreen === 'oneShotResume'
                ? 'One-Shot'
                : currentScreen === 'inspector'
                  ? 'Preflight'
                  : currentScreen}
            </strong>
          </div>
        </aside>

        <main id="app-main-content" className="app-center-castle" tabIndex="-1">
          <div className="App-header">
        <div className="scrollable-screen">
          {paneVisible ? renderCenterPreview() : renderScreen()}
        </div>
      </div>
        </main>
      
      <aside className="app-right-rail" aria-label="Resume controls">
      <div className="sticky-buttons">
        {/* A controls toggle appears on small screens. It toggles the visibility of
            the controls panel. On larger screens this button is hidden via CSS. */}
        <button
          className="controls-toggle"
          ref={toggleRef}
          onClick={() => setControlsExpanded((prev) => !prev)}
        >
          {controlsExpanded ? 'Hide Controls' : 'Show Controls'}
        </button>
        <div
          ref={controlsRef}
          className={`controls-content ${controlsExpanded ? 'expanded' : 'collapsed'}`}
        >
          {/* Top controls shown on small screens (mobile). These include the
              download button and the usage meter so users can access them
          without scrolling. They are hidden on larger screens via CSS. */}
          <div className="mobile-top-controls">
            {showResume && (
              <button onClick={downloadDocx}>Download DOCX</button>
            )}
            {/* {renderTokenMeter()} */}
          </div>

          {/* Button to toggle the visibility of the side pane. When the pane is hidden the
              resume preview and any results or tips are not rendered. */}
          <section className="control-group">
            <div className="control-group-heading">
              <h3>Preview</h3>
              <span>{paneVisible ? 'Visible' : 'Hidden'}</span>
            </div>
            <div className="control-button-stack">
              <button
                className="control-primary-action"
                onClick={() => {
                  if (paneVisible) {
                    setResumeEditable(false);
                  }
                  setShowResume(true);
                  setShowResults(false);
                  setPaneVisible((visible) => !visible);
                }}
              >
                {paneVisible ? 'Hide Resume' : 'Show Resume'}
              </button>
              <button onClick={downloadDocx}>Download DOCX</button>
              {paneVisible && showResume && (
                <button onClick={() => {
                  setResumeEditable((editable) => !editable);
                }}>
                  {resumeEditable ? 'Finish Inline Editing' : 'Edit Inline'}
                </button>
              )}
            </div>
          </section>
          <section className="control-group">
            <div className="control-group-heading">
              <h3>Saved Resume</h3>
            </div>
            <div className="save-slot-dropdown control-field">
              <label htmlFor="resumeSlot">Current Resume: {currentResumeName}</label>
              <select id="resumeSlot" value={selectedResumeSlot} onChange={(e) => {
                handleSlotChange(e);
              }}>
                <option value="base">{getResumeSlotLabel('base')}</option>
                {Array.from({ length: numberOfResumeSlots || 1 }, (_, index) => {
                  const slot = String(index + 1);
                  return (
                    <option key={slot} value={slot}>
                      {getResumeSlotLabel(slot)}
                    </option>
                  );
                })}
              </select>
            </div>

            <label className="control-field" htmlFor="resumeSlotName">
              <span>Slot name</span>
              <input
                id="resumeSlotName"
                type="text"
                maxLength={SLOT_NAME_MAX_LENGTH}
                value={slotNameDraft}
                placeholder={getFallbackResumeSlotLabel(selectedResumeSlot)}
                onChange={(event) => {
                  setSlotNameDraft(event.target.value);
                }}
              />
            </label>

            <div className="action-buttons">
              <button
                onClick={() => {
                  saveResumeSlotName();
                }}
                disabled={slotNameSaving || !cleanedSlotNameDraft || !slotNameChanged}
              >
                {slotNameSaving ? 'Saving...' : 'Save Name'}
              </button>
              <button
                onClick={() => {
                  setSlotNameDraft('');
                  saveResumeSlotName('');
                }}
                disabled={slotNameSaving || !selectedCustomSlotName}
              >
                Reset Name
              </button>
            </div>

            <div className="action-buttons">
              <button onClick={() => {
                handleLoad();
              }}>Load Resume</button>
              <button onClick={() => {
                handleSave();
              }}>Save As</button>
            </div>
          </section>
          {/* <button onClick={() => {
            setCurrentScreen('inspector');
            // Collapse controls when launching one-shot preflight
            setControlsExpanded(false);
          }}>Open One-Shot Preflight</button>

          {/* Resume layout template selector. Choosing a template will rearrange
              the resume sections to match the selected style. The selection
              persists in state via selectedTemplate. */}
          <section className="control-group">
            <div className="control-group-heading">
              <h3>Layout</h3>
            </div>
            <div className="control-field">
              <span id="resumeFormatLabel">Resume format</span>
              <div
                className="resume-format-toggle"
                role="group"
                aria-labelledby="resumeFormatLabel"
              >
                <button
                  type="button"
                  aria-pressed={normalizeResumeFormat(formData.resumeFormat) === RESUME_FORMATS.FEDERAL}
                  className={normalizeResumeFormat(formData.resumeFormat) === RESUME_FORMATS.FEDERAL ? 'active' : ''}
                  onClick={() => setFormData((previous) => ({
                    ...previous,
                    resumeFormat: RESUME_FORMATS.FEDERAL,
                  }))}
                >
                  Federal
                </button>
                <button
                  type="button"
                  aria-pressed={normalizeResumeFormat(formData.resumeFormat) === RESUME_FORMATS.PRIVATE}
                  className={normalizeResumeFormat(formData.resumeFormat) === RESUME_FORMATS.PRIVATE ? 'active' : ''}
                  onClick={() => setFormData((previous) => ({
                    ...previous,
                    resumeFormat: RESUME_FORMATS.PRIVATE,
                  }))}
                >
                  Private
                </button>
              </div>
              <small className="resume-format-help">
                {normalizeResumeFormat(formData.resumeFormat) === RESUME_FORMATS.FEDERAL
                  ? 'Includes federal employment details.'
                  : 'Uses a concise private-sector presentation.'}
              </small>
            </div>
            <label className="control-field" htmlFor="templateSelect">
              <span>Resume style</span>
            <select
              id="templateSelect"
              value={selectedTemplate}
              onChange={(e) => {
                applyTemplate(e.target.value);
              }}
            >
              <option value="professional">Professional</option>
              <option value="student">Student</option>
              <option value="military">Military</option>
            </select>
            </label>

          {/* Section order controls */}
          <div className="control-field">
            <label htmlFor="sectionSelect">Selected section</label>

            <select
              id="sectionSelect"
              value={selectedSection}
              onChange={(e) => {
                setSelectedSection(e.target.value);
              }}
            >
              {(formData.sectionOrder || DEFAULT_SECTION_ORDER).map((sec) => (
                <option key={sec} value={sec}>
                  {SECTION_LABELS[sec] || sec}
                </option>
              ))}
            </select>

            <div className="section-order-actions">
              <button aria-label="Move selected section up" onClick={() => {
                moveSection('up');
              }}>Move up</button>

              <button aria-label="Move selected section down" onClick={() => {
                moveSection('down');
              }}>Move down</button>

              <button onClick={() => {
                resetSectionOrder();
              }}>Reset order</button>
            </div>
          </div>
          </section>

        {/* Logout button: triggers MSAL sign‑out and resets app state.  It
            appears at the bottom of the controls panel so users can
            conveniently log out when finished. */}
        <section className="control-group control-group-account">
        <div className="control-group-heading">
          <h3>Account</h3>
        </div>
        <button
          className="control-logout"
          onClick={async () => {
            try {
              await signOut();
            } catch (err) {
              console.error('Error during sign‑out:', err);
            } finally {
              // Clear authentication state and basic user info
              setAuthenticated(false);
              setFormData(prev => ({ ...prev, user: '' }));
              // Collapse controls to avoid leftover panels on re‑login
              setControlsExpanded(false);
            }
          }}
        >
          Logout
        </button>
        </section>
        </div>
      </div>
      </aside>
      </div>
    </div>
  );
};

export default App;
